import classNames from 'clsx';
import { ReactComponent as ShieldIcon } from 'img/icons/shield.svg';
import throttle from 'lodash.throttle';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { useSelector } from 'react-redux';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { sendProtectedAnalytics } from 'reactApp/ui/ProtectedHint/ProtectedHint.analytics';
import { Tooltip } from 'reactApp/ui/Tooltip/Tooltip';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';

import styles from './ProtectedTooltip.css';
import { ETooltipTheme, IProps } from './ProtectedTooltip.types';

const TOOLTIP_OFFSET = {
    y: 7,
    x: 0,
};

export const ProtectedTooltip = memo<IProps>(({ text, theme = ETooltipTheme.light }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const storage = useSelector(getCurrentStorage);

    const handleClick = useCallback(() => {
        setShowTooltip(true);
        sendProtectedAnalytics({
            action: 'view',
            place: storage,
            platform: 'touch',
        });
    }, [storage]);

    const handleClose = useCallback(() => {
        setShowTooltip(false);
    }, []);

    const throttledScroll = throttle(() => setShowTooltip(false), 100);

    useEffect(() => {
        sendProtectedAnalytics({
            action: 'show',
            place: storage,
            platform: 'touch',
        });
    }, []);

    useEffect(() => {
        document.addEventListener('scroll', throttledScroll);
        return () => document.removeEventListener('scroll', throttledScroll);
    }, [throttledScroll]);

    return (
        <>
            <div className={styles.root} ref={ref} onClick={handleClick}>
                <ShieldIcon />
            </div>
            {showTooltip && (
                <Portal>
                    <Tooltip
                        theme="octavius"
                        target={ref}
                        placement={tooltipPlacements.BOTTOM}
                        minWidth={false}
                        offset={TOOLTIP_OFFSET}
                        closeOnOutsideClick
                        smartPlacement
                        forceAutoPosition
                        onClose={handleClose}
                        arrowColor={theme === ETooltipTheme.dark ? '#333333' : '#ffffff'}
                    >
                        <div
                            className={classNames(styles.tooltip, {
                                [styles.tooltipDark]: theme === ETooltipTheme.dark,
                            })}
                        >
                            {text}
                        </div>
                    </Tooltip>
                </Portal>
            )}
        </>
    );
});

ProtectedTooltip.displayName = 'ProtectedTooltip';
